import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { BillingMilestoneModel, SubcontractorInvoiceModel } from '../project/project.models';
import { DashboardService } from './dashboard.service';
import { AggregatedEntriesByMonth } from './dashboard.model';

/** This file is divided in 3 parts:
 * - The definition of the Store : defining data structure
 * - Constants that allow NgXs to trigger Actions
 * - Selectors & Actions : Functions allowed to interact with the store
 *
 */

// Store definition
export interface DashboardStateModel {
    billingMilestones: BillingMilestoneModel[];
    subcontractorInvoices: SubcontractorInvoiceModel[];
    timeEntriesByActivities: AggregatedEntriesByMonth[];
    timeEntriesByTypologies: AggregatedEntriesByMonth[];
    isLoading: boolean;
    year: number;
}

//Constants

export class GetBillingMilestones {
    static readonly type = 'PROJECTS/BILLINGMILESTONES/ADMIN';
    constructor(public payload: { year?: number }) {}
}

export class GetTimeEntriesByActivities {
    static readonly type = 'PROJECTS/TIMEENTRIES/ACTIVITIES/ADMIN';
    constructor(public payload: { year?: number }) {}
}

export class GetTimeEntriesByTypologies {
    static readonly type = 'PROJECTS/TIMEENTRIES/TYPOLOGIES/ADMIN';
    constructor(public payload: { year?: number }) {}
}

//Actions

@State<DashboardStateModel>({
    name: 'Dashboard',
    defaults: {
        billingMilestones: [],
        subcontractorInvoices: [],
        timeEntriesByActivities: [],
        timeEntriesByTypologies: [],
        isLoading: false,
        year: new Date().getFullYear(),
    },
})
@Injectable()
export class DashboardState {
    @Selector()
    public static billingMilestones(state: DashboardStateModel): BillingMilestoneModel[] {
        return state.billingMilestones;
    }
    @Selector()
    public static subcontractorInvoices(state: DashboardStateModel): SubcontractorInvoiceModel[] {
        return state.subcontractorInvoices;
    }

    @Selector()
    public static timeEntriesByActivities(state: DashboardStateModel): AggregatedEntriesByMonth[] {
        return state.timeEntriesByActivities;
    }
    @Selector()
    public static timeEntriesByTypologies(state: DashboardStateModel): AggregatedEntriesByMonth[] {
        return state.timeEntriesByTypologies;
    }

    @Selector()
    public static isLoading(state: DashboardStateModel): boolean {
        return state.isLoading;
    }
    @Selector()
    public static year(state: DashboardStateModel): number {
        return state.year;
    }

    constructor(
        private dashboardService: DashboardService,
        private readonly store: Store,
    ) {}

    @Action(GetBillingMilestones)
    public async GetBillingMilestones(
        ctx: StateContext<DashboardStateModel>,
        action: GetBillingMilestones,
    ): Promise<void> {
        const state = ctx.getState();
        const year = action.payload.year || state.year;
        ctx.patchState({ isLoading: true });
        const billingMilestones = (await this.dashboardService.getBillingMilestones(year)) || [];
        const subcontractorInvoices =
            (await this.dashboardService.getSubcontractorInvoices(year)) || [];

        ctx.patchState({ billingMilestones, subcontractorInvoices, year, isLoading: false });
    }

    @Action(GetTimeEntriesByActivities)
    public async getTimeEntriesByActivities(
        ctx: StateContext<DashboardStateModel>,
        action: GetTimeEntriesByActivities,
    ): Promise<void> {
        const state = ctx.getState();
        const year = action.payload.year || state.year;
        ctx.patchState({ isLoading: true });
        const timeEntriesByActivities =
            (await this.dashboardService.getEntriesByActivities(year)) || [];
        ctx.patchState({ timeEntriesByActivities, year, isLoading: false });
    }
    @Action(GetTimeEntriesByTypologies)
    public async getTimeEntriesByTypologies(
        ctx: StateContext<DashboardStateModel>,
        action: GetTimeEntriesByTypologies,
    ): Promise<void> {
        const state = ctx.getState();
        const year = action.payload.year || state.year;
        ctx.patchState({ isLoading: true });
        const timeEntriesByTypologies =
            (await this.dashboardService.getEntriesByTypologies(year)) || [];
        ctx.patchState({ timeEntriesByTypologies, year, isLoading: false });
    }
}
